import { useIsAuthenticated, useMsal } from '@azure/msal-react'
import { Fragment, lazy, useEffect } from 'react'
import { useQuery } from 'react-query'
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { ToastContainer } from 'react-toastify'

import { AppContainer, BrandSpinnerWrapper, MainRouteContainer } from 'App.styles'
import { BrandSpinner } from 'asserts/DynamicIcons/BrandSpinner'
import { apiConstants } from 'constants/APIConstants'
import { categoryName, eventName } from 'constants/GAConstants'
import { localStorageConstants } from 'constants/localstorageConstants'
import { roleConstants } from 'constants/roleConstants'
import { EnvironmentType, PageRoutes, accountType, resolvePageRoute } from 'constants/sharedConstants'
import { urlConstants } from 'constants/URLConstants'
import useGoogleAnalytics from 'modules/customHooks/useGoogleAnalytics'
import EnvViewer from 'modules/pages/envViewer'
import { UserSettingsData } from 'modules/pages/manageSettings/managePermission'
import { useAppContext } from 'providers/AppProviders'
import { getFromStorage, setInStorage } from 'services/helper'
import 'react-toastify/dist/ReactToastify.css'
import 'rsuite/dist/rsuite.css'
import './custom.scss'
import { useRequest } from 'services/useRequest'

const Header = lazy(() => import('modules/components/Header/Header'))
const Footer = lazy(() => import('modules/components/Footer/Footer'))
const Login = lazy(() => import('modules/pages/login/Login'))
const Tickets = lazy(() => import('modules/pages/tickets/tickets'))
const TicketDetailsManagement = lazy(() => import('modules/pages/tickets/ticketDetailsManagement'))
const UserManagement = lazy(() => import('modules/pages/userManagement/userManagement'))
const CreateUser = lazy(() => import('modules/pages/userManagement/userList/createUser/createUser'))
const ManageSettings = lazy(() => import('modules/pages/manageSettings/manageSettings'))
const SupplierManagement = lazy(() => import('modules/pages/supplierManagement/supplierManagement'))
const ManagePermission = lazy(() => import('modules/pages/manageSettings/managePermission'))
const Support = lazy(() => import('modules/pages/support/support'))
const NotFound = lazy(() => import('modules/pages/notFound/notFound'))
const PrivateRoute = lazy(() => import('modules/components/privateRoute/privateRoute'))

/**
 * Main app
 */
function App() {
  const { AUTH_TOKEN, LAST_LOGIN_API_TRIGGERED } = localStorageConstants
  const isAuthenticated = useIsAuthenticated()
  const { accounts } = useMsal()
  const { request } = useRequest()
  const { initializeGoogleAnalytics, trackGoogleAnalyticsEvent } = useGoogleAnalytics()
  const { setUserAccessMapping, setSelectedSupplierStartDate } = useAppContext()
  const authToken = process.env?.NODE_ENV === EnvironmentType?.TEST ? accounts?.[0]?.idToken : getFromStorage(AUTH_TOKEN)
  const isAuth = !!authToken || isAuthenticated
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { ADMIN, VENTURE_METAL_STAFF, SUPPLIER_CONSUMER, SUPPLIER_CONSUMER_MAPPING } = roleConstants
  const { USER_DETAILS_API, AUTH, LAST_LOGIN } = apiConstants
  const loginUserDetails = accounts?.[0]?.idTokenClaims
  const loginRole = loginUserDetails?.role as string
  const isDefault = pathname === urlConstants.DEFAULT_PATH
  const isLastLoginCalled = !!getFromStorage(LAST_LOGIN_API_TRIGGERED)
  const currentSelectedSupplier = getFromStorage(localStorageConstants?.CUSTOMER_FILTER) || []

  const { isFetching: isUserPermissionFetching = false, refetch: refetchUserPermission } = useQuery(
    ['getUserPermissions', currentSelectedSupplier],
    () => {
      return request(`${USER_DETAILS_API}`, 'get', {}, process.env?.REACT_APP_USER_URL)
    },
    {
      enabled: !!authToken,
      onSuccess: ({ body }) => {
        const settings = body?.settings || []
        const defaultSupplier = settings?.find((setting: UserSettingsData) => {
          const codeValue = setting?.userDefinedListValue?.CodeValue ?? ''
          return !!codeValue && codeValue !== null && codeValue !== accountType?.FREIGHT
        })
        const selectedSupplierId = currentSelectedSupplier?.[0]?._id || defaultSupplier?.ScrapDragonSupplierID || ''
        const { TicketListingDate = null } =
          settings?.find(({ ScrapDragonSupplierID }: UserSettingsData) => ScrapDragonSupplierID === selectedSupplierId) ??
          ({} as Partial<UserSettingsData>)
        setSelectedSupplierStartDate(TicketListingDate || null)
        setUserAccessMapping(settings)
      },
      initialData: {
        success: true,
        body: {},
      },
    },
  )

  const { isFetching: isLastLoginUpdating = false } = useQuery(
    ['getLastLogin'],
    () => {
      return request(`${AUTH}${LAST_LOGIN}`, 'patch', {}, process.env?.REACT_APP_AUTH_URL)
    },
    {
      enabled: !!authToken && !isLastLoginCalled && !isUserPermissionFetching,
      onSuccess: () => {
        setInStorage(LAST_LOGIN_API_TRIGGERED, true)
        trackGoogleAnalyticsEvent(categoryName?.USER_TYPE, eventName?.IS_INTERNAL_USER, {
          value: loginRole === SUPPLIER_CONSUMER ? 0 : 1,
        })
      },
      initialData: {
        success: true,
        body: {},
      },
    },
  )

  if (window.location.href.includes('server_error')) setInStorage(localStorageConstants.AD_LOGIN_ERROR, window.location.href)

  useEffect(() => {
    if (isAuth || isAuthenticated) {
      !authToken && setInStorage(localStorageConstants.AUTH_TOKEN, accounts?.[0]?.idToken ?? '')
      const path = pathname || urlConstants.TICKETS
      const redirectURL = isDefault && isAuthenticated ? urlConstants.TICKETS : path
      navigate(redirectURL)
    } else {
      navigate(urlConstants.DEFAULT_PATH)
    }
    initializeGoogleAnalytics()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated])

  useEffect(() => {
    const title = PageRoutes[pathname as keyof typeof PageRoutes] ?? resolvePageRoute(pathname)
    if (title && !isDefault && !!authToken) {
      document.title = title
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  if (isUserPermissionFetching || isLastLoginUpdating) {
    return (
      <BrandSpinnerWrapper>
        <BrandSpinner />
      </BrandSpinnerWrapper>
    )
  }
  return (
    <AppContainer className="App" data-testid="App">
      <ToastContainer autoClose={5000} closeOnClick closeButton position={'top-right'} />
      <Header {...{ isAuth }} />
      <MainRouteContainer {...{ isAuth }}>
        <Routes>
          {!isAuth ? (
            <Route path={urlConstants.DEFAULT_PATH} element={<Login />} />
          ) : (
            <Fragment>
              <Route path={urlConstants.TICKETS} element={<Tickets {...{ refetchUserPermission }} />} />
              {!!authToken && (
                <Route path={urlConstants.DEFAULT_PATH} element={<Tickets {...{ refetchUserPermission }} />} />
              )}
              <Route path={urlConstants.TICKET_DETAILS} element={<TicketDetailsManagement />} />
              <Route
                path={urlConstants.USER_MANAGEMENT}
                element={
                  <PrivateRoute
                    component={<UserManagement />}
                    hasPermission={[`${ADMIN}`, `${VENTURE_METAL_STAFF}`, `${SUPPLIER_CONSUMER_MAPPING}`]?.includes(
                      `${loginRole}`,
                    )}
                  />
                }
              />
              {[urlConstants.CREATE_USER, urlConstants.EDIT_USER, urlConstants.VIEW_USER].map((path) => (
                <Route
                  key={path}
                  {...{ path }}
                  element={
                    <PrivateRoute
                      component={<CreateUser />}
                      hasPermission={[`${ADMIN}`, `${VENTURE_METAL_STAFF}`, `${SUPPLIER_CONSUMER_MAPPING}`]?.includes(
                        `${loginRole}`,
                      )}
                    />
                  }
                />
              ))}
              <Route
                path={urlConstants.MANAGE_SETTINGS}
                element={
                  <PrivateRoute component={<ManageSettings />} hasPermission={[`${ADMIN}`]?.includes(`${loginRole}`)} />
                }
              />
              <Route
                path={urlConstants.SUPPLIER_CONSUMER_MAPPING}
                element={
                  <PrivateRoute
                    component={<SupplierManagement />}
                    hasPermission={[`${ADMIN}`, `${VENTURE_METAL_STAFF}`, `${SUPPLIER_CONSUMER_MAPPING}`]?.includes(
                      `${loginRole}`,
                    )}
                  />
                }
              />
              <Route
                path={`${urlConstants.MANAGE_PERMISSION}`}
                element={
                  <PrivateRoute component={<ManagePermission />} hasPermission={[`${ADMIN}`]?.includes(`${loginRole}`)} />
                }
              />
              <Route path={urlConstants.SUPPORT} element={<Support />} />
              <Route path={urlConstants.NOT_FOUND} element={<NotFound />} />
              <Route path={'/env-variables'} element={<EnvViewer />} />
              {!isDefault && <Route path="*" element={<NotFound />} />}
            </Fragment>
          )}
        </Routes>
      </MainRouteContainer>
      <Footer {...{ isAuth }} />
    </AppContainer>
  )
}

export default App
